import { Dispatch, SetStateAction } from "react";
import { isNil } from "client/Misc/utils";

interface IInputReducer<TValue> {
	readonly path: string;
	readonly value: TValue;
	setValue(newValue: TValue): void;
}

interface IObjectFormReducer<TValue> extends IInputReducer<TValue> {
	for<TProperty extends StringKeyOf<TValue>>(propertyName: TProperty): IValueReducer<TValue[TProperty]>;
}

interface ICollectionFormReducer<TCollectionItem, TCollection extends ReadonlyArray<TCollectionItem>> extends
	IInputReducer<TCollection> {
	forIndex(index: number): IValueReducer<TCollectionItem>;
	insertAt(index: number, ...newItems: readonly TCollectionItem[]): void;
	push(...newItems: readonly TCollectionItem[]): void;
	remove(condition: (i: TCollectionItem) => boolean): void;
	removeAt(index: number): void;
}

type IValueReducer<TValue> =
	[TValue] extends [PrimitiveType] ? IInputReducer<TValue> :
	[TValue] extends [ReadonlyArray<infer R>] ? ICollectionFormReducer<R, TValue> :
	IObjectFormReducer<TValue>;

class FormReducer<TCollectionItem, TValue extends ReadonlyArray<TCollectionItem>> implements
	IObjectFormReducer<TValue>,
	ICollectionFormReducer<TCollectionItem, TValue> {

	private readonly _valueAccessor: () => TValue;
	private readonly _onNewValue: (reducer: (currentValue: TValue) => TValue) => void;
	private readonly _propertyReducersCache: Map<StringKeyOf<TValue>, IValueReducer<any>>;
	private readonly _collectionReducersCache: Map<number, IValueReducer<TCollectionItem>>;
	public readonly path: string;

	public get value(): TValue {
		return this._valueAccessor();
	}

	constructor(
		path: string,
		valueAccessor: () => TValue,
		onNewValue: (reducer: (currentValue: TValue) => TValue) => void
	) {
		this.path = path;
		this._valueAccessor = valueAccessor;
		this._onNewValue = onNewValue;
		this._propertyReducersCache = new Map();
		this._collectionReducersCache = new Map();
	}

	public setValue(newValue: TValue|((currentValue: TValue) => TValue)) {
		if (typeof newValue === "function") {
			this._onNewValue(newValue);
		} else {
			this._onNewValue(_ => newValue);
		}
	}

	public for<TProperty extends StringKeyOf<TValue>>(
		propertyName: TProperty
	): IValueReducer<TValue[TProperty]> {
		if (this._propertyReducersCache.has(propertyName)) {
			return this._propertyReducersCache.get(propertyName) as IValueReducer<TValue[TProperty]>;
		}

		const newReducer: IValueReducer<TValue[TProperty]> = this.createReducerForProperty(propertyName);
		this._propertyReducersCache.set(propertyName, newReducer);

		return newReducer;
	}

	public forIndex(index: number): IValueReducer<TCollectionItem> {
		if (this._collectionReducersCache.has(index)) {
			return this._collectionReducersCache.get(index)!;
		}

		const newReducer: IValueReducer<TCollectionItem> = this.createReducerForIndex(index);
		this._collectionReducersCache.set(index, newReducer);

		return newReducer;
	}

	public push(...newItems: readonly TCollectionItem[]): void {
		this._onNewValue(c => [...c, ...newItems] as ReadonlyArray<TCollectionItem> as TValue);
	}

	public insertAt(index: number, ...newItems: readonly TCollectionItem[]): void {
		this._onNewValue(value => {
			const copy: TCollectionItem[] = [...value];

			copy.splice(index, 0, ...newItems);

			return copy as ReadonlyArray<TCollectionItem> as TValue;
		});
	}

	public remove(condition: (i: TCollectionItem) => boolean): void {
		this._onNewValue(c => c.filter(i => !condition(i)) as ReadonlyArray<TCollectionItem> as TValue);
	}

	public removeAt(index: number): void {
		this._onNewValue(value => {
			const copy: TCollectionItem[] = [...value];

			copy.splice(index, 1);
			return copy as ReadonlyArray<TCollectionItem> as TValue;
		});
	}

	private createReducerForProperty<TKey extends StringKeyOf<TValue>>(key: TKey): IValueReducer<TValue[TKey]> {
		const onNewChildValue = (childReducer: (currentValue: TValue[TKey]) => TValue[TKey]) => {
			this._onNewValue(cur => ({ ...cur, [key]: childReducer(cur[key]) }));
		};

		const reducer = new FormReducer<unknown, any>(
			this.path === "" ? key : `${this.path}.${key}`,
			() => {
				const value: TValue = this._valueAccessor();

				if (isNil(value)) {
					throw new Error("Cannot create reducer for a child of not set object");
				}

				this.cleanUpCollectionReducersCache(value);

				return value[key];
			},
			onNewChildValue
		);

		return reducer as unknown as IValueReducer<TValue[TKey]>;
	}

	private createReducerForIndex(index: number): IValueReducer<TCollectionItem> {
		const onNewChildValue = (childReducer: (currentValue: TCollectionItem) => TCollectionItem) => {
			this._onNewValue(value => {
				const copy: TCollectionItem[] = [...value];

				copy.splice(index, 1, childReducer(value[index]));
				return copy as ReadonlyArray<TCollectionItem> as TValue;
			});
		};

		const result = new FormReducer<unknown, any>(
			`${this.path}[${index}]`,
			() => {
				const value: TValue = this._valueAccessor();

				if (isNil(value)) {
					throw new Error("Cannot create reducer for a child of not set object");
				}

				if (!Array.isArray(value)) {
					throw new Error("Cannot create collection reducer for a non-collection object");
				}

				if (value.length < index) {
					throw new Error("Index is out of bound of the collection.");
				}

				return value[index];
			},
			onNewChildValue
		);

		return result as unknown as IValueReducer<TCollectionItem>;
	}

	private cleanUpCollectionReducersCache(newValue: TValue) {
		if (!Array.isArray(newValue)) {
			return;
		}

		const collectionReducersToCleanUp: ReadonlyArray<number> = [...this._collectionReducersCache.keys()]
			.filter(i => i >= newValue.length);

		collectionReducersToCleanUp.forEach(v => this._collectionReducersCache.delete(v));
	}
}

function createFormReducer<TModel>(
	stateAccessor: () => TModel,
	setState: Dispatch<SetStateAction<TModel>>
): IValueReducer<TModel> {
	const reducer = new FormReducer<unknown, any>(
		"",
		stateAccessor,
		setState
	);

	return reducer as unknown as IValueReducer<TModel>;
}

function isInputReducer<TValue>(value: unknown): value is IInputReducer<TValue> {
	return value instanceof FormReducer;
}

export {
	createFormReducer,
	isInputReducer,
	type IInputReducer,
	type IObjectFormReducer,
	type ICollectionFormReducer,
	type IValueReducer,
	FormReducer
};